import { createUseStyles } from 'react-jss';
import { Form, Formik, FormikHelpers } from 'formik';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Captcha, useToast } from '@app/core';
import {
  ApiFormattedException,
  Consent,
  useAcceptConsent,
  useLogin,
} from '@app/api';
import { useCreateClient, useCreateClientWithInvitation } from 'queries';
import { getLocale } from 'i18n';

import {
  AccountFields,
  accountInitialValues,
  accountSchemaFields,
  Checkbox,
  ConsentCheckbox,
  FormikError,
  Input,
  Link,
  PhoneNumberInput,
  PromoCodeInput,
  SubmitButton,
  YupValidators,
} from 'components';
import { useTrackSignUp } from '@app/analytics';

const useStyles = createUseStyles((theme: Theme) => ({
  h2: {
    ...theme.fonts.h2,
    marginBottom: theme.marginBase * 2,
  },
  subtitle: {
    ...theme.fonts.body,
  },
  link: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  alreadyHaveAccount: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase * 5,
    '& > p': {
      marginBottom: theme.marginBase,
    },
  },
  captcha: {
    marginBottom: theme.marginBase * 2,
  },
}));

const accountSchema = Yup.object().shape({
  account: Yup.object().shape(accountSchemaFields),
  newsletterOptin: Yup.boolean().nullable(),
  phoneNumber: YupValidators.isPhoneNumber(),
  jobTitle: Yup.string().required(),
  organization: Yup.object().shape({
    name: Yup.string().required(),
  }),
  consents: Yup.array().required().min(1),
});

type Values = typeof initialValues;

const initialValues = {
  account: accountInitialValues,
  organization: {
    name: '',
  },
  phoneNumber: '',
  jobTitle: '',
  consents: [] as Consent[],
  promoCode: '',
  altcha: '',
};

interface Props {
  email?: string;
  organisationName?: string;
  invitationToken?: string;
  promoCode?: string;
  accountData?: {
    firstName?: string;
    lastName?: string;
  };
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const SignupForm = ({
  accountData,
  email,
  organisationName,
  invitationToken,
  promoCode,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const { mutateAsync: login } = useLogin(true);
  const { mutateAsync: createClient } = useCreateClient();
  const { mutateAsync: createClientWithInvitation } =
    useCreateClientWithInvitation(invitationToken || '');

  const { mutate: addConsentToAccount } = useAcceptConsent();
  const trackSignup = useTrackSignUp();

  initialValues.account.firstName = accountData?.firstName || '';
  initialValues.account.lastName = accountData?.lastName || '';
  initialValues.account.email = email || initialValues.account.email;
  initialValues.organization.name =
    organisationName || initialValues.organization.name;

  const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
    try {
      const {
        consents,
        account: { passwordRepeat, ...account },
        ...client
      } = values;

      const accountWithLocale = { ...account, locale: getLocale() };
      if (invitationToken) {
        await createClientWithInvitation({
          phoneNumber: values.phoneNumber,
          account: accountWithLocale,
          invitationToken,
          jobTitle: client.jobTitle,
        });
      } else {
        await createClient({
          ...client,
          promoCode: values.promoCode,
          account: accountWithLocale,
        });
      }

      let me;
      for (let i = 0; i < 5; i++) {
        try {
          await delay(200);
          me = await login({
            email: account.email,
            password: account.password,
            altcha: values.altcha,
          });
          if (me?.organizations?.length) {
            break;
          }
          await delay(200);
        } catch (e) {}
      }

      if (!me) {
        toast.exception();
        return;
      }

      trackSignup({
        ...me,
        phoneNumber: values.phoneNumber as string,
        type: invitationToken ? 'invited' : 'organic',
        role: 'client',
        jobTitle: values.jobTitle,
        organizationId: me.organizations?.[0].organizationId,
        organizationName: values.organization.name,
      });

      for (const consent of consents) {
        addConsentToAccount(consent);
      }

      navigate('/welcome');
    } catch (e) {
      if (
        e instanceof ApiFormattedException &&
        e.getCode() === 'EMAIL_ALREADY_EXISTS'
      ) {
        helpers.setStatus({
          account: { email: t('form.errors.email.alreadyUsed') },
        });
      } else {
        helpers.setStatus(e);
      }
    }
    helpers.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={accountSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <h2 className={classes.h2}>
          <Trans i18nKey="signup.client.title" />
        </h2>
        <h3 className={classes.subtitle}>
          <Trans i18nKey="signup.client.subtitle" />
        </h3>

        <AccountFields
          namePrefix="account"
          unchangeableFields={{
            email: !!email,
            firstName: !!accountData?.firstName,
            lastName: !!accountData?.lastName,
          }}
        />

        <Input
          name="organization.name"
          label={t('form.organization.name')}
          placeholder="Ex: Apple"
          disabled={!!organisationName}
        />

        <PhoneNumberInput
          name="phoneNumber"
          label={t('form.client.phoneNumber')}
        />

        <Input
          name="jobTitle"
          label={t('form.participant.jobTitle')}
          placeholder={t('form.participant.placeholder.jobTitle')}
        />

        {!invitationToken && <PromoCodeInput code={promoCode} />}

        <ConsentCheckbox name="consents" consent="CLIENT_SIGNUP" />
        <Checkbox
          name="newsletterOption"
          label={t('signup.client.newsletter')}
        />
        <Captcha className={classes.captcha} invisible />

        <FormikError />
        <SubmitButton>
          <Trans i18nKey="signup.client.submit" />
        </SubmitButton>

        <div className={classes.alreadyHaveAccount}>
          <p>
            <Trans i18nKey="signup.client.alreadyHaveAccount" />
          </p>
          <p>
            <Link to="/login" className={classes.link} arrowRight>
              <Trans i18nKey="signup.client.login" />
            </Link>
          </p>
        </div>
      </Form>
    </Formik>
  );
};
